/**
 * Free-text product search for the unified "Zu Produkt hinzufügen" overlay.
 *
 * Wraps `getProducts({ search })` (the same endpoint the legacy
 * TransferToProductDialog already used) which matches SKU, GTIN AND name —
 * unlike the identifier-only `/api/v2/products/search` (`useProductsSearch`),
 * which returns 0 matches for any free-text/name query.
 *
 * Return shape is intentionally compatible with `ProductSearchResult` so the
 * AddToProductProductList consumer renders without other changes. The endpoint
 * does not surface a `matched_field`, so we always return `null` for it — the
 * "Vorgefiltert nach SKU/GTIN/ASIN" badge silently goes away (acceptable: the
 * user can see what they typed and which match was picked).
 *
 * Debounced 250ms; disabled when the query is empty so clearing the field does
 * not spam the backend.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProducts } from '@/lib/api/products'
import type { ProductSearchMatch, ProductSearchResult } from '@/lib/api/products-search'

function useDebouncedString(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

export function useProductsAnySearch(query: string) {
  const debounced = useDebouncedString(query, 250)
  return useQuery<ProductSearchResult>({
    queryKey: ['products-any-search', debounced],
    queryFn: async () => {
      const res = await getProducts({ search: debounced, page_size: 50 })
      const matches: ProductSearchMatch[] = (res.products ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
        gtin: p.gtin,
        asin: p.asin,
        workspace_integration_id: p.workspace_integration_id,
      }))
      return { matches, matched_field: null }
    },
    enabled: debounced.length > 0,
    staleTime: 30_000,
  })
}
